import React, { useContext, useMemo, useState } from "react";
import { FiCheck, FiChevronLeft, FiCreditCard, FiLock, FiTruck } from "react-icons/fi";
import { Link } from "react-router-dom";
import productData from "../Datas/productData";
import siteConfig, { formatPrice } from "../config/siteConfig";
import { CartContext } from "./CartContext";
import PageMeta from "./PageMeta";
import "../css/checkout.css";

const initialForm = {
  email: "",
  firstName: "",
  lastName: "",
  country: "Hong Kong",
  address: "",
  apartment: "",
  district: "",
  region: "",
  delivery: "next-day",
  instructions: "",
};

const requiredFields = ["email", "firstName", "lastName", "country", "address", "district"];
const availableCountries = [
  "Hong Kong", "Australia", "Austria", "Belgium", "Canada", "Denmark", "Finland",
  "France", "Germany", "Ireland", "Italy", "Japan", "Luxembourg", "Malaysia",
  "Netherlands", "New Zealand", "Norway", "Portugal", "Singapore", "South Korea",
  "Spain", "Sweden", "Switzerland", "Taiwan", "Thailand", "United Kingdom", "United States",
];

const validate = (form) => {
  const errors = {};
  requiredFields.forEach((field) => {
    if (!form[field].trim()) errors[field] = "This field is required.";
  });
  if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) errors.email = "Enter a valid email address.";
  return errors;
};

const Field = ({ label, name, error, children }) => (
  <div className={`checkout-field ${error ? "has-error" : ""}`}>
    <label htmlFor={name}>{label}</label>
    {children}
    {error && <span className="field-error" id={`${name}-error`}>{error}</span>}
  </div>
);

const CheckOut = () => {
  const { cartItems } = useContext(CartContext);
  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const lines = useMemo(
    () => cartItems
      .map((item) => ({ ...item, product: productData.find((product) => product.id === item.id) }))
      .filter((item) => item.product),
    [cartItems]
  );
  const subtotal = lines.reduce((sum, item) => sum + item.product.price * item.quantity, 0);
  const shipping = siteConfig.delivery.standardFee;
  const total = subtotal + shipping;

  const change = (event) => {
    const { name, value } = event.target;
    setForm((current) => ({ ...current, [name]: value }));
    if (errors[name]) setErrors((current) => ({ ...current, [name]: "" }));
  };

  const placeOrder = (event) => {
    event.preventDefault();
    const nextErrors = validate(form);
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) {
      window.setTimeout(() => document.querySelector(".has-error input, .has-error select")?.focus(), 0);
      return;
    }

    setSubmitting(true);
    const randomPart = window.crypto?.getRandomValues
      ? Array.from(window.crypto.getRandomValues(new Uint32Array(3))).join("")
      : `${Date.now()}${Math.floor(Math.random() * 1e9)}`;
    const orderNumber = randomPart.slice(0, 28);
    const origin = window.location.origin;
    const params = new URLSearchParams({
      orderNo: orderNumber,
      amount: total.toFixed(2),
      currency: siteConfig.currency.code,
      email: form.email,
      success_url: `${origin}/payment?state=success`,
      cancel_url: `${origin}/payment?state=cancel`,
      title: lines[0].product.name,
    });
    window.location.assign(`${origin}/create_payment.php?${params.toString()}`);
  };

  if (!lines.length) {
    return (
      <main className="empty-state-page">
        <PageMeta title="Checkout" />
        <div className="empty-state-card">
          <p className="eyebrow">Checkout</p>
          <h1>Your cart is empty.</h1>
          <p>Add something to your chilled bag before checking out.</p>
          <Link className="button button--primary" to="/shop">Go to the shop</Link>
        </div>
      </main>
    );
  }

  return (
    <main className="checkout-page">
      <PageMeta title="Secure checkout" />
      <div className="container checkout-heading">
        <Link to="/cartpage"><FiChevronLeft /> Back to cart</Link>
        <div><p className="eyebrow">Almost at your table</p><h1>Secure checkout</h1></div>
        <span><FiLock /> Encrypted</span>
      </div>

      <form className="container checkout-layout" onSubmit={placeOrder} noValidate>
        <div className="checkout-form">
          <section className="checkout-section">
            <div className="checkout-section__title"><span>1</span><div><h2>Contact</h2><p>We’ll send order and delivery updates here.</p></div></div>
            <Field label="Email address" name="email" error={errors.email}>
              <input id="email" name="email" type="email" value={form.email} onChange={change} autoComplete="email" aria-describedby={errors.email ? "email-error" : undefined} />
            </Field>
          </section>

          <section className="checkout-section">
            <div className="checkout-section__title"><span>2</span><div><h2>Delivery address</h2><p>Enter the address exactly as it should appear for delivery.</p></div></div>
            <div className="checkout-field-row">
              <Field label="First name" name="firstName" error={errors.firstName}>
                <input id="firstName" name="firstName" value={form.firstName} onChange={change} autoComplete="given-name" />
              </Field>
              <Field label="Last name" name="lastName" error={errors.lastName}>
                <input id="lastName" name="lastName" value={form.lastName} onChange={change} autoComplete="family-name" />
              </Field>
            </div>
            <Field label="Country / region" name="country" error={errors.country}>
              <select id="country" name="country" value={form.country} onChange={change} autoComplete="country-name">
                {availableCountries.map((country) => <option key={country}>{country}</option>)}
              </select>
            </Field>
            <Field label="Street and building" name="address" error={errors.address}>
              <input id="address" name="address" value={form.address} onChange={change} autoComplete="street-address" />
            </Field>
            <Field label="Apartment, floor, unit (optional)" name="apartment">
              <input id="apartment" name="apartment" value={form.apartment} onChange={change} />
            </Field>
            <div className="checkout-field-row">
              <Field label="District" name="district" error={errors.district}>
                <input id="district" name="district" value={form.district} onChange={change} autoComplete="address-level2" />
              </Field>
              <Field label="State / region" name="region">
                <input id="region" name="region" value={form.region} onChange={change} autoComplete="address-level1" />
              </Field>
            </div>
          </section>

          <section className="checkout-section">
            <div className="checkout-section__title"><span>3</span><div><h2>Delivery</h2><p>Choose the most convenient window.</p></div></div>
            <label className={`delivery-option ${form.delivery === "next-day" ? "is-active" : ""}`}>
              <input type="radio" name="delivery" value="next-day" checked={form.delivery === "next-day"} onChange={change} />
              <FiTruck /><span><b>Next-day chilled delivery</b><small>10am–6pm · order before {siteConfig.delivery.orderCutoff}</small></span><strong>{shipping ? formatPrice(shipping) : "Free"}</strong>
            </label>
            <label className={`delivery-option ${form.delivery === "weekend" ? "is-active" : ""}`}>
              <input type="radio" name="delivery" value="weekend" checked={form.delivery === "weekend"} onChange={change} />
              <FiTruck /><span><b>Saturday chilled delivery</b><small>10am–4pm · plan your gathering</small></span><strong>{shipping ? formatPrice(shipping) : "Free"}</strong>
            </label>
            <Field label="Delivery instructions (optional)" name="instructions">
              <textarea id="instructions" name="instructions" rows="3" value={form.instructions} onChange={change} placeholder="Gate code, reception details or a safe handover note" />
            </Field>
          </section>

          <section className="checkout-section">
            <div className="checkout-section__title"><span>4</span><div><h2>Payment</h2><p>You will continue to our secure payment provider.</p></div></div>
            <label className="payment-choice is-active">
              <input type="radio" name="payment" value="card" checked readOnly />
              <FiCreditCard /><span>Credit or debit card</span><small>Secure redirect</small>
            </label>
          </section>
        </div>

        <aside className="checkout-summary">
          <h2>Your order <span>{lines.reduce((count, line) => count + line.quantity, 0)}</span></h2>
          <div className="checkout-summary__lines">
            {lines.map((line) => (
              <div className="checkout-line" key={line.lineKey}>
                <div><img src={line.product.image} alt="" /><span>{line.quantity}</span></div>
                <p><b>{line.product.name}</b><small>{line.option || line.product.unit}</small></p>
                <strong>{formatPrice(line.product.price * line.quantity)}</strong>
              </div>
            ))}
          </div>
          <div className="checkout-totals">
            <div><span>Subtotal</span><strong>{formatPrice(subtotal)}</strong></div>
            <div><span>Delivery</span><strong>{shipping ? formatPrice(shipping) : "Free"}</strong></div>
            <div className="checkout-total"><span>Total <small>{siteConfig.currency.code}</small></span><strong>{formatPrice(total)}</strong></div>
          </div>
          <button className="button button--primary place-order-button" type="submit" disabled={submitting}>
            {submitting ? "Opening secure payment…" : <>Pay now · {formatPrice(total)}</>}
          </button>
          <p className="checkout-legal"><FiCheck /> By placing the order you accept our <Link to="/terms">terms</Link> and <Link to="/privacy">privacy policy</Link>.</p>
        </aside>
      </form>
    </main>
  );
};

export default CheckOut;
